import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { TTSButton } from './TTSButton';
import { LEVEL_DATA, ALL_UNIQUE_WORDS } from '../data/gameData';
import { WordItem } from '../types';
import { PlayerSprite, BerberRugBorder } from './Sprites';
import { playSound } from '../audio';
import { getWordIcon } from '../utils/emojis';
import { ScrollDualScreenReader } from './ScrollDualScreenReader';
import { CrosswordChallengeView } from './CrosswordChallengeView';
import { UnifiedQuizChallengeModal } from './UnifiedQuizChallengeModal';
import { getChallengeWords } from '../utils/challengeWords';
import { ChallengeItem, CHALLENGES_LIST } from '../data/challengesData';
import { generateCrosswordForChallenge, generateCrosswordGridsForChallenge, MAX_WORDS_PER_GRID } from '../utils/crosswordGenerator';
import { CrosswordGrid } from '../data/crosswordData';
import { getCustomWords, subscribeCustomWords } from '../utils/customVocabularyStorage';
import { subscribeWordOverrides } from '../utils/wordOverridesStorage';
import { getAllWordQuizStatuses, subscribeWordQuizStatuses, WordQuizStatus } from '../utils/quizWordStatusStorage';
import {
  getActiveQuizId,
  setActiveQuizId,
  getActiveCrosswordId,
  setActiveCrosswordId,
} from '../utils/activeActivityManager';

interface ChallengesViewProps {
  score: number;
  setScore: React.Dispatch<React.SetStateAction<number>>;
  onBack: () => void;
  unlockedLevels: number[];
  onStartCelestialChallenge?: () => void;
  onOpenDictionary?: () => void;
  isAdmin?: boolean;
}

interface SkinItem {
  id: string;
  name: string;
  tier: string;
  stageNum: number;
  cost: number;
  desc: string;
  badge: string;
}

const SKINS: SkinItem[] = [
  { 
    id: 'default', 
    name: 'Aqcic (Héros)', 
    tier: 'Initié', 
    stageNum: 0, 
    cost: 0, 
    desc: "L'enfant de la région, gardien de la mémoire kabyle.",
    badge: '🌱 Départ'
  },
  { 
    id: 'uccen', 
    name: 'Uccen (Chacal)', 
    tier: 'Étape 1 • Bronze', 
    stageNum: 1, 
    cost: 1500, 
    desc: "Rapide et rusé, l'animal mythique de nos contes traditionnels.",
    badge: '🥉 1 500 XP'
  },
  { 
    id: 'teryel', 
    name: 'Teryel (Ogresse)', 
    tier: 'Étape 2 • Argent', 
    stageNum: 2, 
    cost: 4500, 
    desc: "Créature magique et imposante des légendes anciennes.",
    badge: '🥈 4 500 XP'
  },
  { 
    id: 'ayyis', 
    name: 'Ayyis (Cheval)', 
    tier: 'Étape 3 • Or', 
    stageNum: 3, 
    cost: 10000, 
    desc: "Fier destrier des hautes crêtes du Djurdjura.",
    badge: '🥇 10 000 XP'
  },
  { 
    id: 'igider', 
    name: 'Igider (Aigle)', 
    tier: 'Étape 4 • Légendaire', 
    stageNum: 4, 
    cost: 22000, 
    desc: "Le seigneur suprême des cieux kabyles. Réserve ultime des grands maîtres.",
    badge: '👑 22 000 XP'
  }
];

const ChallengeIconWithGauge: React.FC<{
  icon: string;
  isUnlocked: boolean;
  isValidated: boolean;
  progressRatio: number;
}> = ({ icon, isUnlocked, isValidated, progressRatio }) => {
  const size = 48;
  const strokeWidth = 3.5;
  const radius = (size - strokeWidth) / 2; // 22.25
  const circumference = 2 * Math.PI * radius; // ~139.8
  const clampedRatio = Math.min(1, Math.max(0, progressRatio));
  const strokeDashoffset = circumference * (1 - clampedRatio);

  return (
    <div className="relative w-12 h-12 flex items-center justify-center shrink-0">
      {/* Circular Progress Gauge SVG */}
      <svg
        className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Background Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={isUnlocked ? '#e7decb' : '#e2e8f0'}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Active Progress Arc */}
        {isUnlocked && clampedRatio > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#10b981"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        )}
      </svg>

      {/* Center Icon & Validation Overlay */}
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center relative overflow-hidden transition-all shadow-inner ${
          !isUnlocked
            ? 'bg-stone-200 grayscale opacity-60 text-xl'
            : isValidated
            ? 'bg-emerald-600 shadow-emerald-700/30'
            : 'bg-amber-50/90 text-xl'
        }`}
      >
        {/* Base Theme Emoji/Icon */}
        <span
          className={`transition-all select-none ${
            isValidated ? 'opacity-20 filter blur-[0.5px] text-lg' : 'text-xl'
          }`}
        >
          {icon}
        </span>

        {/* Validated Green Filter & White Checkmark Icon On Top */}
        {isValidated && (
          <div className="absolute inset-0 bg-emerald-600/90 flex items-center justify-center text-white">
            <svg
              className="w-5 h-5 text-white stroke-[3.5]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export const ChallengesView: React.FC<ChallengesViewProps> = ({
  score, setScore, onBack, unlockedLevels, onStartCelestialChallenge, onOpenDictionary, isAdmin = false
}) => {

  // Live dictionary synchronization state
  const [vocabVersion, setVocabVersion] = useState(0);
  const [showFreeAccessOverlay, setShowFreeAccessOverlay] = useState(false);
  const [selectedLockedChallenge, setSelectedLockedChallenge] = useState<ChallengeItem | null>(null);

  useEffect(() => {
    const unsubCustom = subscribeCustomWords(() => {
      setVocabVersion(v => v + 1);
    });
    const unsubOverrides = subscribeWordOverrides(() => {
      setVocabVersion(v => v + 1);
    });
    return () => {
      unsubCustom();
      unsubOverrides();
    };
  }, []);

  const customWordsList = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ver = vocabVersion;
    return getCustomWords();
  }, [vocabVersion]);

  const [readingScroll, setReadingScroll] = useState<{title: string, content: string} | null>(null);

  // Active Challenge minigame state (Unified Quiz)
  const [activeQuizChallenge, setActiveQuizChallenge] = useState<ChallengeItem | null>(null);

  // Crossword completion tracking state
  const [solvedCrosswordIds, setSolvedCrosswordIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('tamazight_crosswords_solved_grids');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Perfect Quiz tracking state (sans-fautes)
  const [perfectQuizIds, setPerfectQuizIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('tamazight_quiz_perfect_challenges');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Word quiz validation statuses (Green/Red pastilles)
  const [wordStatuses, setWordStatuses] = useState<Record<string, WordQuizStatus>>(() => getAllWordQuizStatuses());

  useEffect(() => {
    const unsubscribe = subscribeWordQuizStatuses((updated) => {
      setWordStatuses(updated);
    });
    return unsubscribe;
  }, []);

  const refreshProgressData = useCallback(() => {
    try {
      const rawCrosswords = localStorage.getItem('tamazight_crosswords_solved_grids');
      setSolvedCrosswordIds(rawCrosswords ? JSON.parse(rawCrosswords) : []);
    } catch {}
    try {
      const rawQuiz = localStorage.getItem('tamazight_quiz_perfect_challenges');
      setPerfectQuizIds(rawQuiz ? JSON.parse(rawQuiz) : []);
    } catch {}
    setWordStatuses(getAllWordQuizStatuses());
  }, []);

  useEffect(() => {
    window.addEventListener('storage', refreshProgressData);
    window.addEventListener('tamazight_crosswords_updated', refreshProgressData);
    window.addEventListener('tamazight_quiz_updated', refreshProgressData);
    return () => {
      window.removeEventListener('storage', refreshProgressData);
      window.removeEventListener('tamazight_crosswords_updated', refreshProgressData);
      window.removeEventListener('tamazight_quiz_updated', refreshProgressData);
    };
  }, [refreshProgressData]);

  // Helper to compute total and completed grids for each challenge
  const getChallengeCrosswordStats = useCallback((challenge: ChallengeItem) => {
    const words = getChallengeWords(challenge.category);
    const wordCount = words.length;
    const totalGrids = Math.max(1, Math.ceil(wordCount / MAX_WORDS_PER_GRID));
    let completedGrids = 0;

    if (totalGrids === 1) {
      if (
        solvedCrosswordIds.includes(`grid_challenge_${challenge.id}`) ||
        (challenge.id === 'phrases' && solvedCrosswordIds.includes('grid_challenge_dialogue'))
      ) {
        completedGrids++;
      }
    } else {
      for (let p = 1; p <= totalGrids; p++) {
        if (
          solvedCrosswordIds.includes(`grid_challenge_${challenge.id}_part${p}`) ||
          (challenge.id === 'phrases' && solvedCrosswordIds.includes(`grid_challenge_dialogue_part${p}`))
        ) {
          completedGrids++;
        }
      }
    }

    return { wordCount, totalGrids, completedGrids };
  }, [solvedCrosswordIds]);

  // Helper to compute overall theme validation status and progression gauge ratio
  const getChallengeStatus = useCallback((challenge: ChallengeItem) => {
    const crosswordStats = getChallengeCrosswordStats(challenge);
    const isQuizPerfect =
      perfectQuizIds.includes(challenge.id) ||
      (challenge.id === 'phrases' && perfectQuizIds.includes('dialogue'));
    const isCrosswordsDone = crosswordStats.totalGrids > 0 && crosswordStats.completedGrids === crosswordStats.totalGrids;
    const isValidated = isQuizPerfect && isCrosswordsDone;

    const totalSteps = 1 + crosswordStats.totalGrids;
    const completedSteps = (isQuizPerfect ? 1 : 0) + crosswordStats.completedGrids;
    const progressRatio = totalSteps > 0 ? completedSteps / totalSteps : 0;
    const progressPercent = Math.round(progressRatio * 100);

    return {
      ...crosswordStats,
      isQuizPerfect,
      isCrosswordsDone,
      isValidated,
      totalSteps,
      completedSteps,
      progressRatio,
      progressPercent,
    };
  }, [getChallengeCrosswordStats, perfectQuizIds]);

  // Overall stats for challenges
  const overallChallengeStats = useMemo(() => {
    let total = 0;
    let validated = 0;

    CHALLENGES_LIST.forEach((ch) => {
      total++;
      const status = getChallengeStatus(ch);
      if (status.isValidated) validated++;
    });

    if (customWordsList.length > 0) {
      total++;
      const customChallenge: ChallengeItem = {
        id: 'custom_vocab',
        category: 'Personnalisé',
        title: 'Mes Mots Personnalisés',
        icon: '📝',
        region: 'Amawal Personnel',
        requiredLevel: 0,
        npcName: 'Mon Dictionnaire',
        desc: '',
      };
      const customStatus = getChallengeStatus(customChallenge);
      if (customStatus.isValidated) validated++;
    }

    return {
      total,
      validated,
      percent: total > 0 ? Math.round((validated / total) * 100) : 0,
    };
  }, [getChallengeStatus, customWordsList.length]);

  // Active Challenge Crossword state
  const [activeCrosswordChallenge, setActiveCrosswordChallenge] = useState<ChallengeItem | null>(null);
  const [activeCrosswordGrid, setActiveCrosswordGrid] = useState<CrosswordGrid | null>(null);
  const [activeCrosswordGrids, setActiveCrosswordGrids] = useState<CrosswordGrid[]>([]);

  const [activeSavedQuizId, setActiveSavedQuizId] = useState<string | null>(getActiveQuizId);
  const [activeSavedCrosswordId, setActiveSavedCrosswordId] = useState<string | null>(getActiveCrosswordId);

  useEffect(() => {
    const syncQuiz = () => setActiveSavedQuizId(getActiveQuizId());
    const syncCrossword = () => setActiveSavedCrosswordId(getActiveCrosswordId());
    syncQuiz();
    syncCrossword();
    window.addEventListener('tamazight_active_quiz_changed', syncQuiz);
    window.addEventListener('tamazight_active_crossword_changed', syncCrossword);
    const onStorage = () => {
      syncQuiz();
      syncCrossword();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('tamazight_active_quiz_changed', syncQuiz);
      window.removeEventListener('tamazight_active_crossword_changed', syncCrossword);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const [expandedChallengeId, setExpandedChallengeId] = useState<string | null>(null);

  // Start a Quiz challenge
  const startChallenge = (challenge: ChallengeItem) => {
    setActiveQuizId(challenge.id);
    playSound('click');
    setActiveQuizChallenge(challenge);
  };

  // Start a Crossword challenge (dynamically generated with 100% of category keywords, chunked at 50 max)
  const startCrosswordChallenge = (challenge: ChallengeItem) => {
    setActiveCrosswordId(challenge.id);
    playSound('click');
    const words = getChallengeWords(challenge.category);
    if (words.length === 0) return;

    const grids = generateCrosswordGridsForChallenge(
      challenge.id,
      challenge.title,
      challenge.category,
      words,
      {
        icon: challenge.icon,
        region: challenge.region,
        requiredLevel: challenge.requiredLevel,
      }
    );

    setActiveCrosswordChallenge(challenge);
    setActiveCrosswordGrids(grids);
    setActiveCrosswordGrid(grids[0] || null);
  };

  const isFullscreenView = Boolean(activeQuizChallenge || activeCrosswordGrid);

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-parchment text-stone-900 flex flex-col font-sans z-[10000] overflow-hidden select-none">
      {/* Top Berber Woven Rug Border */}
      <BerberRugBorder />

      {/* Header */}
      {!isFullscreenView && (
        <header className="py-2 px-3 sm:px-4 bg-[#fcf8f0] border-b border-[#dfd2be] shadow-xs flex flex-col gap-2.5 flex-shrink-0 z-30">
          {/* Topbar */}
          <div className="flex justify-between items-center w-full">
            <button 
              type="button"
              onClick={() => {
                playSound('click');
                onBack();
              }}
              className="px-3.5 py-1.5 bg-amber-900 hover:bg-amber-800 text-amber-50 font-black text-xs uppercase tracking-wider border border-amber-700 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              <svg className="w-3 h-3 fill-amber-50 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="17,4 7,12 17,20" />
              </svg>
              <span>Retour</span>
            </button>

            <div className="flex items-center gap-2 bg-[#ede4d4] px-3 py-1.5 rounded-full border border-[#d6c7b0] shadow-inner">
              <span className="text-[10px] text-stone-500 font-bold uppercase tracking-widest">XP</span>
              <span className="text-amber-900 font-black text-lg font-mono">{score}</span>
            </div>
          </div>
        </header>
      )}

      {/* Content */}
      <div className={`flex-1 min-h-0 overscroll-contain ${activeCrosswordGrid ? 'overflow-hidden p-0' : 'overflow-y-auto overflow-x-hidden px-3.5 py-3 sm:px-6 sm:py-4 space-y-3 sm:space-y-4 w-full box-border'}`}>
        {/* ======================================================== */}
        {/* ======================================================== */}
        {/* CHALLENGES / DÉFIS (QUIZZ & MOTS-CROISÉS)                */}
        {/* ======================================================== */}
        {!activeCrosswordGrid && (
          <div className="grid gap-3 max-w-2xl mx-auto w-full min-w-0 box-border">
            {/* ======================================================== */}
            {/* BLOCS CONTINUER EN COURS (QUIZZ & MOTS-CROISÉS)          */}
            {/* ======================================================== */}
            {(() => {
              const allChallenges = [
                ...CHALLENGES_LIST,
                ...(customWordsList.length > 0
                  ? [
                      {
                        id: 'custom_vocab',
                        title: 'Mon Vocabulaire Personnalisé',
                        category: 'CUSTOM_VOCAB',
                        icon: '⭐',
                        region: 'Sanctuaire Personnel',
                        requiredLevel: 0,
                        npcName: 'Mon Dictionnaire',
                        desc: 'Défiez-vous sur vos propres mots et expressions ajoutés dans le Dictionnaire.',
                      },
                    ]
                  : []),
              ];

              const activeSavedQuizChallenge = activeSavedQuizId
                ? allChallenges.find((c) => c.id === activeSavedQuizId) || null
                : null;
              const activeSavedCrosswordChallenge = activeSavedCrosswordId
                ? allChallenges.find((c) => c.id === activeSavedCrosswordId) || null
                : null;

              if (!activeSavedQuizChallenge && !activeSavedCrosswordChallenge) return null;

              return (
                <div className="space-y-2.5 w-full min-w-0 box-border">
                  {activeSavedQuizChallenge && (
                    <div className="p-3 sm:p-3.5 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white rounded-2xl shadow-md border-2 border-amber-400 flex items-center justify-between gap-2.5 sm:gap-3 w-full min-w-0 box-border overflow-hidden animate-pulse-subtle">
                      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/20 border border-white/40 flex items-center justify-center text-lg sm:text-xl shrink-0">
                          {activeSavedQuizChallenge.icon || '⚡'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-amber-100 block truncate">
                            Quizz en cours non terminé
                          </span>
                          <h4 className="text-xs sm:text-sm font-black truncate leading-tight">
                            {activeSavedQuizChallenge.title}
                          </h4>
                          <span className="text-[10px] sm:text-[11px] text-amber-100/90 font-medium block truncate">
                            {activeSavedQuizChallenge.npcName} • {activeSavedQuizChallenge.region}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => startChallenge(activeSavedQuizChallenge)}
                        className="bg-white hover:bg-amber-50 text-amber-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl font-black text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all shrink-0 cursor-pointer active:scale-95 flex items-center gap-1 sm:gap-1.5"
                      >
                        <span>Reprendre</span>
                        <span>➔</span>
                      </button>
                    </div>
                  )}

                  {activeSavedCrosswordChallenge && (
                    <div className="p-3 sm:p-3.5 bg-gradient-to-r from-teal-600 via-teal-700 to-teal-800 text-white rounded-2xl shadow-md border-2 border-teal-400 flex items-center justify-between gap-2.5 sm:gap-3 w-full min-w-0 box-border overflow-hidden animate-pulse-subtle">
                      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/20 border border-white/40 flex items-center justify-center text-lg sm:text-xl shrink-0">
                          {activeSavedCrosswordChallenge.icon || '🧩'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-teal-100 block truncate">
                            Mots-Croisés en cours non terminé
                          </span>
                          <h4 className="text-xs sm:text-sm font-black truncate leading-tight">
                            {activeSavedCrosswordChallenge.title}
                          </h4>
                          <span className="text-[10px] sm:text-[11px] text-teal-100/90 font-medium block truncate">
                            {activeSavedCrosswordChallenge.npcName} • {activeSavedCrosswordChallenge.region}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => startCrosswordChallenge(activeSavedCrosswordChallenge)}
                        className="bg-white hover:bg-teal-50 text-teal-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl font-black text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all shrink-0 cursor-pointer active:scale-95 flex items-center gap-1 sm:gap-1.5"
                      >
                        <span>Reprendre</span>
                        <span>➔</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Progression des Défis */}
            <div className="p-3.5 sm:p-4 bg-[#fffdf9] border-2 border-[#dfd2be] rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs w-full min-w-0 box-border overflow-hidden">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 border-2 border-amber-300 flex items-center justify-center text-2xl shrink-0 shadow-inner">
                  🎯
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm sm:text-base font-black text-amber-950 uppercase tracking-wide truncate">
                    Progression des Défis
                  </h3>
                  <p className="text-[11px] sm:text-xs text-stone-600 max-w-md leading-tight mt-0.5">
                    Validez chaque thématique en réalisant un <strong className="text-amber-900 font-bold">sans-faute au Quizz</strong> (0 erreur) et en complétant toutes les <strong className="text-amber-900 font-bold">grilles de mots-croisés</strong> (+500 XP par grille).
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      playSound('click');
                      setSelectedLockedChallenge(null);
                      setShowFreeAccessOverlay(true);
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-100/90 hover:bg-amber-200 text-amber-950 border border-amber-300/80 text-[11px] font-black transition-all cursor-pointer shadow-2xs active:scale-95"
                  >
                    <span>💡</span>
                    <span>Accès libre aux défis via le Dictionnaire</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto shrink-0 bg-[#f4ece0] px-3.5 py-2 rounded-xl border border-[#dfd2be] shadow-inner">
                <div className="text-right">
                  <span className="text-[9px] text-stone-500 block uppercase font-black tracking-wider">Défis Validés</span>
                  <span className="text-base sm:text-lg font-black text-amber-950 font-mono">
                    {overallChallengeStats.validated} / {overallChallengeStats.total}
                  </span>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-300 flex items-center justify-center text-xs font-black text-emerald-800 font-mono ml-1">
                  {overallChallengeStats.percent}%
                </div>
              </div>
            </div>

            {/* ======================================================== */}
            {/* ULTIME DÉFI : L'ASCENSION VERS LA LUNE (50 QUESTIONS)     */}
            {/* ======================================================== */}
            {(() => {
              const isCelestialUnlocked = unlockedLevels.includes(4) || unlockedLevels.includes(5);

              return (
                <div className={`p-3.5 sm:p-4 rounded-2xl border-2 transition-all flex flex-col gap-2.5 relative overflow-hidden w-full min-w-0 box-border ${
                  isCelestialUnlocked
                    ? 'bg-[#fffdf9] border-amber-500 shadow-md'
                    : 'bg-[#f7f2ea]/70 border-[#e0d4c3] opacity-70'
                }`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-2xl shrink-0 border-2 ${
                        isCelestialUnlocked
                          ? 'bg-amber-100 border-amber-300 shadow-xs'
                          : 'bg-stone-200 border-stone-300 grayscale'
                      }`}>
                        🌙
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                            ★ Ultime Défi ★
                          </span>
                        </div>
                        <h3 className={`font-black text-sm sm:text-base mt-0.5 truncate ${isCelestialUnlocked ? 'text-amber-900' : 'text-stone-400'}`}>
                          L'Ascension vers la Lune
                        </h3>
                        <p className="text-[10px] text-stone-600 font-mono truncate">
                          Jedjiga (La Sorcière) • 5 Vies
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      {isCelestialUnlocked ? (
                        <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-lg font-mono font-bold shadow-2xs">
                          50 Mots • Chrono ⏱️
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 bg-stone-200 text-stone-500 border border-stone-300 rounded font-mono">
                          Niveau 5 Requis 🔒
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-1.5 border-t border-[#dfd2be]">
                    {isCelestialUnlocked ? (
                      <button
                        onClick={() => {
                          playSound('click');
                          if (onStartCelestialChallenge) {
                            onStartCelestialChallenge();
                          }
                        }}
                        className="w-full py-2.5 px-4 bg-amber-700 hover:bg-amber-800 text-white font-black text-xs sm:text-sm uppercase tracking-wider rounded-xl shadow-xs active:translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer border border-amber-600"
                      >
                        <span>🚀</span>
                        <span>{activeSavedQuizId === 'ascension' ? 'Continuer l\'Ascension' : 'Lancer l\'Ascension (50 Quizz)'}</span>
                      </button>
                    ) : (
                      <div className="text-[11px] text-stone-500 italic flex items-center gap-1.5">
                        <span>🔒</span>
                        <span>Se débloque après avoir terminé le Niveau 5</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* CARTE DÉFI PERSONNALISÉ (Mes Mots Personnalisés) */}
            {customWordsList.length > 0 && (() => {
              const customWordsCount = customWordsList.length;
              const customChallenge: ChallengeItem = {
                id: 'custom_vocab',
                category: 'Personnalisé',
                title: 'Mes Mots Personnalisés',
                icon: '📝',
                region: 'Amawal Personnel',
                requiredLevel: 0,
                npcName: 'Mon Dictionnaire',
                desc: '',
              };
              const status = getChallengeStatus(customChallenge);

              return (
                <div 
                  className="p-3.5 sm:p-4 rounded-xl border-2 border-[#dfd2be] hover:border-amber-400 bg-white/95 shadow-xs flex flex-col gap-2 cursor-pointer transition-all"
                  onClick={() => setExpandedChallengeId(expandedChallengeId === 'custom_vocab' ? null : 'custom_vocab')}
                >
                  <div className="flex items-center justify-between gap-3 pointer-events-none">
                    <div className="flex items-center gap-3 min-w-0">
                      <ChallengeIconWithGauge
                        icon="📝"
                        isUnlocked={true}
                        isValidated={status.isValidated}
                        progressRatio={status.progressRatio}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-sm sm:text-base text-stone-900">
                            Mes Mots Personnalisés
                          </h3>
                        </div>
                        <p className="text-[11px] text-emerald-800 font-mono font-medium">
                          Amawal Personnel • Mon Vocabulaire
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-1.5 sm:gap-2">
                      {status.isValidated && (
                        <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-900 border border-emerald-400 rounded-md font-black flex items-center gap-1">
                          ✓ Validé
                        </span>
                      )}
                      <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-900 border border-emerald-300 rounded font-mono font-bold">
                        {customWordsCount} mots
                      </span>
                      <div className="flex items-center gap-1 text-[11px] sm:text-xs font-black text-emerald-950 bg-emerald-100/90 hover:bg-emerald-200 border border-emerald-300 rounded-lg px-2 sm:px-2.5 py-1 transition-all shadow-2xs">
                        <span className="hidden xs:inline">{expandedChallengeId === 'custom_vocab' ? 'Replier' : 'Déplier'}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedChallengeId === 'custom_vocab' ? 'rotate-180 text-emerald-950' : 'text-emerald-800'}`} />
                      </div>
                    </div>
                  </div>

                  {expandedChallengeId === 'custom_vocab' && (
                    <div className="mt-2 pt-2 border-t border-emerald-300 cursor-default" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between text-[11px] font-bold text-emerald-950 mb-1.5">
                        <span>Pastilles des mots :</span>
                        <span className="text-[10px] text-emerald-800">
                          {customWordsList.filter((w) => wordStatuses[w.kab] === 'CORRECT').length}/{customWordsCount} validés 🟢
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3 max-h-40 overflow-y-auto pr-1 no-scrollbar">
                        {customWordsList.map((word, i) => {
                          const wStatus = wordStatuses[word.kab];
                          return (
                            <div
                              key={i}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] sm:text-xs border transition-colors ${
                                wStatus === 'CORRECT'
                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-950'
                                  : wStatus === 'WRONG'
                                  ? 'bg-rose-50 border-rose-300 text-rose-950'
                                  : 'bg-emerald-50/50 border-emerald-200 text-stone-900'
                              }`}
                            >
                              <span
                                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  wStatus === 'CORRECT'
                                    ? 'bg-emerald-500 shadow-[0_0_4px_#10b981]'
                                    : wStatus === 'WRONG'
                                    ? 'bg-rose-500 shadow-[0_0_4px_#f43f5e]'
                                    : 'bg-stone-300'
                                }`}
                              />
                              <span className="font-bold text-stone-900">{word.kab}</span>
                              <span className="text-stone-500 italic">({word.fr})</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1.5 border-t border-[#dfd2be] w-full min-w-0 box-border" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => startChallenge(customChallenge)}
                      className={`py-2 sm:py-2.5 px-3 font-black text-xs uppercase tracking-wider rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer border ${
                        status.isQuizPerfect
                          ? 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-600'
                          : 'bg-amber-800 hover:bg-amber-900 text-white border-amber-700'
                      }`}
                    >
                      <span>⚡</span>
                      <span>{activeSavedQuizId === customChallenge.id ? 'Continuer' : `Quizz (${customWordsCount})`}</span>
                      {status.isQuizPerfect ? (
                        <span className="text-[9px] bg-emerald-900/60 text-emerald-100 px-1.5 py-0.5 rounded font-black">
                          Sans-Faute ✓
                        </span>
                      ) : (
                        <span className="text-[9px] bg-amber-950/40 text-amber-100 px-1.5 py-0.5 rounded font-medium">
                          À faire
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => startCrosswordChallenge(customChallenge)}
                      className={`py-2 sm:py-2.5 px-3 font-black text-xs uppercase tracking-wider rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer border ${
                        status.isCrosswordsDone
                          ? 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-600'
                          : 'bg-amber-600 hover:bg-amber-700 text-white border-amber-500'
                      }`}
                    >
                      <span>🧩</span>
                      <span>
                        {activeSavedCrosswordId === customChallenge.id ? 'Continuer' : `Mots-Croisés (${status.completedGrids}/${status.totalGrids})`}
                      </span>
                      {status.isCrosswordsDone && <span className="text-white font-black">✓</span>}
                    </button>
                  </div>
                </div>
              );
            })()}

            {CHALLENGES_LIST.map(challenge => {
              const isUnlocked = unlockedLevels.includes(challenge.requiredLevel);
              const wordsForChallenge = getChallengeWords(challenge.category);
              const wordCount = wordsForChallenge.length;
              const status = getChallengeStatus(challenge);

              return (
                <div
                  key={challenge.id}
                  className={`p-3.5 sm:p-4 rounded-xl border-2 transition-all flex flex-col gap-2 cursor-pointer ${
                    isUnlocked
                      ? 'bg-white/95 border-[#dfd2be] hover:border-amber-400 shadow-xs'
                      : 'bg-[#f7f2ea]/70 border-[#e0d4c3] opacity-75 hover:border-amber-400/80 hover:bg-[#fffdf9]'
                  }`}
                  onClick={() => {
                    playSound('click');
                    if (isUnlocked) {
                      setExpandedChallengeId(expandedChallengeId === challenge.id ? null : challenge.id);
                    } else {
                      setSelectedLockedChallenge(challenge);
                      setShowFreeAccessOverlay(true);
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-3 pointer-events-none">
                    <div className="flex items-center gap-3 min-w-0">
                      <ChallengeIconWithGauge
                        icon={challenge.icon}
                        isUnlocked={isUnlocked}
                        isValidated={status.isValidated}
                        progressRatio={status.progressRatio}
                      />

                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-black text-sm sm:text-base ${isUnlocked ? 'text-stone-900' : 'text-stone-400'}`}>
                            {challenge.title}
                          </h3>
                        </div>
                        <p className="text-[11px] text-stone-500 font-mono font-medium">
                          {challenge.npcName} • {challenge.region}
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-1.5 sm:gap-2">
                      {isUnlocked ? (
                        <>
                          {status.isValidated && (
                            <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-900 border border-emerald-400 rounded-md font-black flex items-center gap-1">
                              ✓ Validé
                            </span>
                          )}
                          <span className="text-[10px] px-2 py-0.5 bg-[#ede4d4] text-amber-900 border border-[#d6c7b0] rounded font-mono font-bold">
                            {wordCount} mots
                          </span>
                          <div className="flex items-center gap-1 text-[11px] sm:text-xs font-black text-amber-950 bg-amber-100/90 hover:bg-amber-200 border border-amber-300/90 rounded-lg px-2 sm:px-2.5 py-1 transition-all shadow-2xs">
                            <span className="hidden xs:inline">{expandedChallengeId === challenge.id ? 'Replier' : 'Déplier'}</span>
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedChallengeId === challenge.id ? 'rotate-180 text-amber-950' : 'text-amber-800'}`} />
                          </div>
                        </>
                      ) : (
                        <span className="text-[10px] px-2.5 py-1 bg-stone-200 text-stone-600 border border-stone-300 rounded-lg font-mono font-bold">
                          Verrouillé 🔒
                        </span>
                      )}
                    </div>
                  </div>

                  {expandedChallengeId === challenge.id && isUnlocked && (
                    <div className="mt-2 pt-2 border-t border-[#dfd2be] cursor-default" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between text-[11px] font-bold text-stone-900 mb-1.5">
                        <span>Pastilles des mots :</span>
                        <span className="text-[10px] text-stone-600">
                          {wordsForChallenge.filter((w) => wordStatuses[w.kab] === 'CORRECT').length}/{wordCount} validés 🟢
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3 max-h-40 overflow-y-auto pr-1 no-scrollbar">
                        {wordsForChallenge.map((word, i) => {
                          const wStatus = wordStatuses[word.kab];
                          return (
                            <div
                              key={i}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] sm:text-xs border transition-colors ${
                                wStatus === 'CORRECT'
                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-950'
                                  : wStatus === 'WRONG'
                                  ? 'bg-rose-50 border-rose-300 text-rose-950'
                                  : 'bg-[#f4ede2] border-[#dcd0be] text-stone-900'
                              }`}
                            >
                              <span
                                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  wStatus === 'CORRECT'
                                    ? 'bg-emerald-500 shadow-[0_0_4px_#10b981]'
                                    : wStatus === 'WRONG'
                                    ? 'bg-rose-500 shadow-[0_0_4px_#f43f5e]'
                                    : 'bg-stone-300'
                                }`}
                              />
                              <span className="font-bold text-stone-900">{word.kab}</span>
                              <span className="text-stone-500 italic">({word.fr})</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-1.5 border-t border-[#dfd2be] w-full min-w-0 box-border" onClick={(e) => e.stopPropagation()}>
                    {isUnlocked ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full min-w-0 box-border">
                        <button
                          type="button"
                          onClick={() => startChallenge(challenge)}
                          className={`py-2 sm:py-2.5 px-3 font-black text-xs uppercase tracking-wider rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer border ${
                            status.isQuizPerfect
                              ? 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-600'
                              : 'bg-amber-800 hover:bg-amber-900 text-white border-amber-700'
                          }`}
                        >
                          <span>⚡</span>
                          <span>{activeSavedQuizId === challenge.id ? 'Continuer' : `Quizz (${wordCount})`}</span>
                          {status.isQuizPerfect ? (
                            <span className="text-[9px] bg-emerald-900/60 text-emerald-100 px-1.5 py-0.5 rounded font-black">
                              Sans-Faute ✓
                            </span>
                          ) : (
                            <span className="text-[9px] bg-amber-950/40 text-amber-100 px-1.5 py-0.5 rounded font-medium">
                              À faire
                            </span>
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => startCrosswordChallenge(challenge)}
                          className={`py-2 sm:py-2.5 px-3 font-black text-xs uppercase tracking-wider rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer border ${
                            status.isCrosswordsDone
                              ? 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-600'
                              : 'bg-amber-600 hover:bg-amber-700 text-white border-amber-500'
                          }`}
                        >
                          <span>🧩</span>
                          <span>
                            {activeSavedCrosswordId === challenge.id ? 'Continuer' : `Mots-Croisés (${status.completedGrids}/${status.totalGrids})`}
                          </span>
                          {status.isCrosswordsDone && <span className="text-white font-black">✓</span>}
                        </button>
                      </div>
                    ) : (
                      <div className="text-[11px] text-stone-500 italic">
                        🔒 Se débloque au niveau {challenge.region}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ======================================================== */}
        {/* ACTIVE UNIFIED QUIZ CHALLENGE MODAL                      */}
        {/* ======================================================== */}
        {activeQuizChallenge && (
          <UnifiedQuizChallengeModal
            challengeId={activeQuizChallenge.id}
            title={`Défi : ${activeQuizChallenge.title}`}
            subtitle={`${activeQuizChallenge.npcName} • ${activeQuizChallenge.region}`}
            icon={activeQuizChallenge.icon}
            words={getChallengeWords(activeQuizChallenge.category)}
            allWords={ALL_UNIQUE_WORDS}
            onClose={() => {
              setActiveQuizChallenge(null);
              refreshProgressData();
            }}
            onAddScore={(xp) => setScore((s) => s + xp)}
            initialHearts={5}
          />
        )}

        {/* ======================================================== */}
        {/* ACTIVE CROSSWORD CHALLENGE MODE                          */}
        {/* ======================================================== */}
        {activeCrosswordGrid && (
          <div className="w-full h-full flex flex-col">
            <CrosswordChallengeView
              score={score}
              setScore={setScore}
              onAddScore={(xp) => setScore((s) => Math.max(0, s + xp))}
              onBack={() => {
                setActiveCrosswordGrid(null);
                setActiveCrosswordChallenge(null);
                setActiveCrosswordGrids([]);
                refreshProgressData();
              }}
              unlockedLevels={unlockedLevels}
              challengeGrid={activeCrosswordGrid}
              challengeGrids={activeCrosswordGrids}
              challengeTitle={activeCrosswordChallenge?.title}
            />
          </div>
        )}

        {/* Scroll Dual Screen Reader */}
        {readingScroll && (
          <ScrollDualScreenReader
            scroll={readingScroll}
            onClose={() => setReadingScroll(null)}
            isAdmin={isAdmin}
          />
        )}

        {/* Free Access via Dictionary Explanatory Overlay */}
        <AnimatePresence>
          {showFreeAccessOverlay && (
            <motion.div
              key="free-access-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setShowFreeAccessOverlay(false)}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-xs select-none"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                transition={{ type: 'spring', stiffness: 380, damping: 26 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-[#fffdf9] border-2 border-amber-500/90 rounded-2xl shadow-2xl p-5 sm:p-6 flex flex-col gap-4 text-stone-900 relative"
              >
                {/* Header Icon + Title */}
                <div className="flex items-start gap-3.5">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-2xl shadow-xs shrink-0 border border-amber-300">
                    📖
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 inline-block mb-1">
                      💡 Astuce Entraînement
                    </span>
                    <h3 className="text-base sm:text-lg font-black text-amber-950 font-cinzel leading-tight">
                      Accès Libre via le Dictionnaire
                    </h3>
                    <p className="text-xs text-stone-600 mt-0.5 font-medium">
                      {selectedLockedChallenge
                        ? `Défi « ${selectedLockedChallenge.title} » (${selectedLockedChallenge.region})`
                        : 'Entraînez-vous sans attendre la progression du mode aventure'}
                    </p>
                  </div>
                </div>

                {/* Body Explanation */}
                <div className="space-y-2.5 text-xs text-stone-700 bg-[#f9f4ec] border border-[#e8ddcb] rounded-xl p-3.5 leading-relaxed">
                  <p>
                    {selectedLockedChallenge ? (
                      <>
                        Cette région est encore verrouillée dans le mode aventure, mais <strong>tous les mots de cette thématique sont déjà accessibles librement</strong> !
                      </>
                    ) : (
                      <>
                        Si vous souhaitez accéder directement aux défis et vous entraîner sans faire le mode aventure, vous pouvez le faire directement dans le <strong>Dictionnaire (Amawal)</strong> :
                      </>
                    )}
                  </p>

                  <div className="space-y-2 pt-1">
                    <div className="flex items-start gap-2">
                      <span className="text-base shrink-0 mt-0.5">📁</span>
                      <div>
                        <strong className="text-amber-950 font-bold block">
                          1. Par Catégorie Thématique
                        </strong>
                        <span>
                          Rendez-vous dans le Dictionnaire, ouvrez la catégorie {selectedLockedChallenge ? <strong className="text-amber-900">« {selectedLockedChallenge.category} »</strong> : 'de votre choix'} et cliquez sur le bouton <strong className="text-amber-900">« Faire le Quizz »</strong> en haut de page.
                        </span>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <span className="text-base shrink-0 mt-0.5">❤️</span>
                      <div>
                        <strong className="text-amber-950 font-bold block">
                          2. Sur Mesure (Mots Favoris)
                        </strong>
                        <span>
                          Dans le dictionnaire, cliquez sur l'icône cœur <strong className="text-rose-600">❤️</strong> à côté des mots sur lesquels vous désirez vous entraîner, puis lancez le quizz de vos favoris.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-center gap-2 pt-1">
                  {onOpenDictionary && (
                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setShowFreeAccessOverlay(false);
                        onOpenDictionary();
                      }}
                      className="w-full sm:flex-1 py-2.5 px-4 bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-700 hover:to-amber-800 text-white font-black text-xs sm:text-sm uppercase tracking-wider rounded-xl shadow-xs active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 border border-amber-950"
                    >
                      <span>📖</span>
                      <span>Ouvrir le Dictionnaire</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      playSound('click');
                      setShowFreeAccessOverlay(false);
                    }}
                    className={`w-full ${onOpenDictionary ? 'sm:w-auto' : 'sm:flex-1'} py-2.5 px-4 bg-[#ede4d4] hover:bg-[#e2d6c2] text-stone-800 font-bold text-xs uppercase tracking-wider rounded-xl border border-[#d6c7b0] active:scale-95 transition-all cursor-pointer text-center`}
                  >
                    Compris ✓
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </div>
);
};
